import React, { useEffect, useRef, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { push } from 'connected-react-router';
import {
  Column,
  Paging,
  SearchPanel,
  Sorting,
  FilterPanel,
  HeaderFilter,
  FilterBuilder
} from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import {
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Button
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import { withStyles } from '@material-ui/core/styles';
import isPlainObject from 'lodash/isPlainObject';
import { columnSchema } from './gridConfig';
import {
  setPostAcqWorkflowGridStateUpdate,
  createExploreSystemView,
  appNotify,
  setIsRedirectToExplore
} from 'store/actions';
import { importModuleStateSelector } from 'store/selectors';
import { dataGridExtension } from 'components/devExtreme';
import DataGrid from 'components/devExtreme/DataGrid';
import CustomColumnChooser from 'components/customColumnChooser';
import { sendClientLog } from 'services/logService';
import { Constants } from 'utilities/constants';
import { GetAsgPropIdsWithImportId } from 'api/revue/import';
import { postWithToken } from 'api/network';

const VIEW_EXPLORE = 'View Explore';
const STRING = 'string';
const VIEW_WORKFLOW = 'View Workflow';
const ID = 'id';

const styles = theme => ({
  gridPaper: {
    padding: '5px 8px 8px',
    overflow: 'hidden',
    width: '100%'
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    // width: '66%'
  },
  title: {
    margin: '5px 0 8px 0'
  },
  refreshButton: {
    marginLeft: 8
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%'
  },
  flexContainer: {
    display: 'flex'
  }
});

const ImportTaskHistoryGrid = ({
  classes,
  setPostAcqWorkflowGridStateUpdate,
  push,
  createExploreSystemView,
  loading,
  setLoading,
  appNotify,
  importHistoryRefresh,
}) => {
  const [dataSource, setDataSource] = useState(null);
  const [isColumnChooserVisible, setIsColumnChooserVisible] = useState(false);
  const gridRef = useRef(null);

  useEffect(() => {
    createDataSource();
    setLoading({ isLoading: true, message: 'Fetching Post-Acquisition Task Import History...' });
  }, [importHistoryRefresh]);

  function createDataSource() {
    const url = '/api/postAcquisition/import/history';

    const store = new CustomStore({
      key: ID,
      load: function (loadOptions) {
        if (isPlainObject(loadOptions.group)) {
          loadOptions.group = [loadOptions.group];
        }

        let params = {};

        dataGridExtension.customStoreRequestParams.forEach(i => {
          if (i in loadOptions && dataGridExtension.isNotEmpty(loadOptions[i])) {
            params[i] = loadOptions[i];
          }
        });

        return postWithToken(url, params)
          .then(response => response.json())
          .then((data) => {
            return {
              data: data.data,
              totalCount: data.totalCount,
            };
          })
          .catch((error) => {
            appNotify(`Failed to fetch Post-Acquisition Task Import History: ${error.message}`, 'error');
            return { data: [], totalCount: 0 };
          })
          .finally(() => {
            setLoading({ isLoading: false, message: null });
          })
      },
    });

    setDataSource(store);
  }

  const getViewWorkflowColumnRender = cell => {
    return (
      <div>
        <Button
          size="small"
          color="primary"
          onClick={() => handleRedirectToChannelWorkflow(cell.data)}
        >
          View On Workflow
        </Button>
      </div>
    )
  }

  const getViewExploreColumnRender = cell => {
    return (
      <div>
        <Button
          size="small"
          color="primary"
          onClick={() => handleRedirectToExplore(cell.data)}
        >
          View On Explore
        </Button>
      </div>
      )
  }

  function handleRedirectToChannelWorkflow(importRecord) {
    // Stash the filter rather than setting grid state directly: on a cold load the workflow grid isn't
    // mounted yet, and its layout-select saga would overwrite a direct setPostAcqWorkflowGridState. The
    // grid folds this pending update into its layout apply once mounted (see PostAcqWorkflowGrid).
    setPostAcqWorkflowGridStateUpdate({ filterValue: [['importId', '=', importRecord.id]], columnMods: [{ field: 'id', type: 'sortOrder', value: 'asc' }] }, true);
    push(Constants.Routes.POSTACQ_WORKFLOW);
  }

  function handleRedirectToExplore(importRecord) {
    GetAsgPropIdsWithImportId(importRecord.id, Constants.Module.POSTACQ).then(asgPropIDs => {
      // construct filterValue
      const filterValue = ['asgPropID', 'in', asgPropIDs];
      // construct filterExpression to display on UI
      const filterExpression = [];
      asgPropIDs.forEach(id => {
        filterExpression.push(['asgPropID', '=', id]);
        filterExpression.push('or');
      });
      filterExpression.pop();
      const newFilter = { filterValue, filterExpression };
      createExploreSystemView(`Post-Acq Task Import ${importRecord.id}`, newFilter);
      push(Constants.Routes.EXPLORE);
    }).catch(error => {
      console.log(error.message);
    });
  }

  const refreshButtonClick = () => {
    createDataSource();
    sendClientLog('Info', 'Post-Acq Task Import History - User started data fetch - refresh button');
  }

  const onToolbarPreparing = e => {
    // this adds a column chooser icon so we can customize its behaviour
    e.toolbarOptions.items.unshift({
      widget: 'dxButton',
      location: 'after',
      options: {
        icon: 'columnchooser',
        elementAttr: {
          id: 'importPostAcqTaskHistoryGridColumnChooserBtn'
        },
        onClick: () => setIsColumnChooserVisible(true)
      }
    });
  }

  // solution from devextreme support team https://supportcenter.devexpress.com/ticket/details/t998890/datagrid-column-headerfilter-customization
  function calculateFilterExpression(
    filterValue,
    selectedFilterOperation,
    target
  ) {
    if (target === 'headerFilter') {
      return [this.dataField, selectedFilterOperation, filterValue];
    }

    return this.defaultCalculateFilterExpression.apply(this, arguments);
  }

  const columns = useMemo(() => {
    const columnList = columnSchema.map(({
      field,
      displayName,
      tooltip,
      width,
      type,
      format,
      visible,
      sortIndex,
      sortOrder,
    }) => {
      // original solutions for custom implementation from devextreme support team
      // https://supportcenter.devexpress.com/ticket/details/t1014077/datagrid-header-filter-performance-for-large-client-side-dataset
      // https://supportcenter.devexpress.com/ticket/details/t1004692/datagrid-searching-does-not-work-when-header-filter-date-values-are-ungrouped
      const headerDataSource = dataGridExtension.createHeaderDataSource('/api/postAcquisition/import/history', type, field, gridRef);

      return (
        <Column
          key={field}
          dataField={field}
          caption={displayName}
          tooltip={tooltip}
          minWidth={width}
          dataType={type}
          format={format}
          visible={visible !== false}
          sortIndex={sortIndex}
          sortOrder={sortOrder}
          calculateFilterExpression={calculateFilterExpression}
        >
          <HeaderFilter dataSource={headerDataSource} groupInterval={null} />
        </Column>
      );
    });

    columnList.push(
      (<Column
        key="viewWorkflow"
        dataField="viewWorkflow"
        caption={VIEW_WORKFLOW}
        minWidth={40}
        dataType={STRING}
        visible={true}
        cellRender={getViewWorkflowColumnRender}
        allowFiltering={false}
        allowSorting={false}
      />),
      (<Column
        key="viewExplore"
        dataField="viewExplore"
        caption={VIEW_EXPLORE}
        minWidth={40}
        dataType={STRING}
        visible={true}
        cellRender={getViewExploreColumnRender}
        allowFiltering={false}
        allowSorting={false}
      />)
    )

    return columnList;
  }, [dataSource]);

  const fields = useMemo(() => {
    return dataGridExtension.sortFields(columnSchema);
  }, [columnSchema]);

  return (
    <div className={classes.container}>
      <div className={classes.titleContainer}>
        <div className={classes.flexContainer}>
          <Typography variant='h5' className={classes.title}>Post-Acquisition Task Import History</Typography>
          <Tooltip title='Refresh Data'>
            <span>
              <IconButton disabled={loading.isLoading} onClick={refreshButtonClick} className={classes.refreshButton}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>
      <Paper className={classes.gridPaper}>
        <DataGrid
          id={'postAcqTaskHistoryGrid'}
          ref={gridRef}
          persistState
          hoverStateEnabled={true}
          dataSource={dataSource}
          focusedRowEnabled={false}
          height={'calc(95vh - 140px)'}
          remoteOperations={true}
          onToolbarPreparing={onToolbarPreparing}
          enableTooltips
          tooltips={columnSchema}
        >
          <HeaderFilter visible={true} search={{ enabled: true }} hideSelectAllOnSearch={false} />
          <Sorting mode={'multiple'} />
          <FilterPanel visible={true} />
          <FilterBuilder fields={fields}>
            {dataGridExtension.filterBuilderCustomOperations}
          </FilterBuilder>
          <Paging
            defaultPageSize={100}
            enabled={true}
          />
          <SearchPanel visible={true} searchVisibleColumnsOnly={true} />
          {columns}
        </DataGrid>
        <CustomColumnChooser
          container={`#postAcqTaskHistoryGrid`}
          visible={isColumnChooserVisible}
          onHiding={() => setIsColumnChooserVisible(false)}
          columns={columns}
          gridRef={gridRef}
        />
      </Paper>
    </div>
  )
}

const mapStateToProps = state => {
  const importState = importModuleStateSelector(state, Constants.Module.POSTACQ);
  return {
    importHistoryRefresh: importState.importHistoryRefresh
  }
}

const mapDispatchToProps = {
  push,
  setPostAcqWorkflowGridStateUpdate,
  setIsRedirectToExplore,
  createExploreSystemView,
  appNotify
}

export default compose(withStyles(styles), connect(mapStateToProps, mapDispatchToProps))(ImportTaskHistoryGrid);
